import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type SystemKind =
  | 'testoffice'
  | 'manuell'
  | 'robot'
  | 'junit'
  | 'pipeline'
  | 'runner'
  | 'github'
  | 'gitlab'
  | 'jenkins'
  | 'azure'
  | 'redmine';

const GLYPH: Record<SystemKind, string> = {
  testoffice: 'tO',
  manuell: 'M',
  robot: 'R',
  junit: 'JU',
  pipeline: 'CI',
  runner: 'LA',
  github: 'GH',
  gitlab: 'GL',
  jenkins: 'J',
  azure: 'AZ',
  redmine: 'RM',
};

@Component({
  selector: 'app-system-badge',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="system-badge" [attr.data-kind]="kind()" [attr.data-size]="size()" data-testid="system-badge">
      <span class="system-badge__glyph" aria-hidden="true">{{ glyph() }}</span>
      <span class="system-badge__label">{{ label() }}</span>
    </span>
  `,
  styles: `
    :host {
      display: inline-flex;
    }

    .system-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
    }

    .system-badge__glyph {
      width: 1.5rem;
      height: 1.5rem;
      border-radius: 0.375rem;
      color: #ffffff;
      display: grid;
      place-items: center;
      font-size: 0.625rem;
      font-weight: 700;
      flex-shrink: 0;
      letter-spacing: -0.02em;
      background: var(--to-warn-fg);
    }

    .system-badge[data-size='sm'] .system-badge__glyph {
      width: 1.25rem;
      height: 1.25rem;
      font-size: 0.5625rem;
    }

    .system-badge[data-kind='testoffice'] .system-badge__glyph {
      background: var(--to-brand);
    }
    .system-badge[data-kind='robot'] .system-badge__glyph {
      background: var(--to-red-dot);
    }
    .system-badge[data-kind='junit'] .system-badge__glyph {
      background: var(--to-junit);
    }
    .system-badge[data-kind='pipeline'] .system-badge__glyph,
    .system-badge[data-kind='runner'] .system-badge__glyph {
      background: var(--to-pipeline);
    }
    .system-badge[data-kind='github'] .system-badge__glyph {
      background: var(--to-github);
    }
    .system-badge[data-kind='gitlab'] .system-badge__glyph {
      background: var(--to-gitlab);
    }
    .system-badge[data-kind='jenkins'] .system-badge__glyph {
      background: var(--to-jenkins);
    }
    .system-badge[data-kind='azure'] .system-badge__glyph {
      background: var(--to-azure);
    }
    .system-badge[data-kind='redmine'] .system-badge__glyph {
      background: var(--to-redmine);
    }

    .system-badge__label {
      font-size: 0.8125rem;
      color: var(--to-text);
    }
  `,
})
export class SystemBadgeComponent {
  readonly kind = input.required<SystemKind>();
  readonly label = input.required<string>();
  readonly size = input<'sm' | 'md'>('md');
  readonly glyph = computed(() => GLYPH[this.kind()]);
}
